import React from 'react';
import {toast} from "react-toastify";
import {FaExternalLinkAlt} from "react-icons/fa";
import analysisToolService from "../../../../services/web/analysis-tool-service.js";
import ItemToken from "./item-token.jsx";
import {getItemMeta} from "./analysis-items.js";
import Button from "../../../../components/button/button.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {format, parseISO} from "date-fns";

const BASENAME = import.meta.env.BASE_URL;
const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB.replace(/^\//, "");

const formatUtcDateTime = (value) => format(parseISO(`${value.replace(" ", "T")}Z`), "dd/MM/yyyy HH:mm");

const ActionButton = ({onClick, children}) => (
    <Button
        target={"primary"}
        onClick={onClick}
        className="mt-3 flex w-full items-center justify-center gap-2 h-9"
    >
        <FaExternalLinkAlt size={11}/> {children}
    </Button>
);

const MarkerDetails = ({marker, boName, onClose}) => {
    const {type, title, description, id} = marker;
    const meta = getItemMeta(type);

    const navigate = navigateHook();

    const openOccurrence = (occId) => {
        analysisToolService.openSentryviewOccurrence(occId)
            .then(() => {
                toast.success(`A ocorrência ${occId} será aberta no SentryView`)
            })
            .catch(error => {
                console.error(error);
                let message = 'Ocorreu um erro ao abrir a ocorrência no SentryView.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                toast.error(message);
            });
    }

    const openWazeMap = (lat, lng) => {
        window.open(`https://www.waze.com/partnerhub/map-tool?lon=${lng}&lat=${lat}`, 'blank_');
    }

    const contentDetails = () => {
        switch (type) {
            case "sentry-occurrence": {
                let {address = '', number = '', neighborhood = '', zipCode = '', city = ''} = description.local || {};

                const addressToString = [
                    address,
                    number && (address ? " - " : "") + number,
                    zipCode && (address || number ? ", " : "") + zipCode,
                    neighborhood && (zipCode || address || number ? ", " : "") + neighborhood,
                    city && (address || number || zipCode || neighborhood ? ", " : "") + city,
                ].filter(Boolean).join('');

                const vehicles = description.vehicles
                    .map(vehicle => vehicle.plate.replace(/(\w{3})(\w{4})/, "$1-$2"))
                    .join(', ');

                return (
                    <>
                        <div className="space-y-2">
                            {
                                vehicles && <p><b>Veículos:</b> {vehicles}</p>
                            }
                            {
                                (description.dt_ini !== null && description.dt_end !== null) &&
                                <p><b>Fato ocorrido entre:</b> {description.dt_ini} à {description.dt_end}</p>
                            }
                            {
                                (description.dt_ini !== null && description.dt_end === null) &&
                                <p><b>Fato ocorrido:</b> {description.dt_ini}</p>
                            }
                            {
                                addressToString &&
                                <p className="truncate" title={addressToString}><b>Endereço:</b> {addressToString}</p>
                            }
                            <p><b>Visibilidade:</b> {description.system_visibility}</p>
                        </div>

                        <ActionButton onClick={() => openOccurrence(id)}>Abrir ocorrência</ActionButton>
                    </>
                );
            }
            case "dispatch-attendance":
                return (
                    <>
                        <p className="truncate" title={description.address}><b>Endereço:</b> {description.address}</p>
                        <ActionButton onClick={() => window.open(`${BASENAME}${API_DESPACHO_WEB}/attendance/form/${id}`)}>
                            Abrir atendimento
                        </ActionButton>
                    </>
                );
            case "dispatch-bogcm":
                return (
                    <>
                        <p className="truncate" title={description.address}><b>Endereço:</b> {description.address}</p>
                        <ActionButton onClick={() => window.open(`${BASENAME}bo/redirect/${id.replace("/", "-")}`)}>
                            Abrir {boName}
                        </ActionButton>
                    </>
                );
            case "dispatch-dispatches":
                return (
                    <>
                        <p className="truncate" title={description.address}><b>Endereço:</b> {description.address}</p>
                        <ActionButton onClick={() => window.open(`${BASENAME}${API_DESPACHO_WEB}/dispatch/form/${id}`)}>
                            Abrir despacho
                        </ActionButton>
                    </>
                );
            case "point-items": {
                const lanes = description.lanes ? JSON.parse(description.lanes) : null;
                return (
                    <div className="space-y-2">
                        {
                            description.address &&
                            <p className="truncate" title={description.address}><b>Endereço:</b> {description.address}</p>
                        }
                        {
                            description.description && <p><b>Observação:</b> {description.description}</p>
                        }
                        {
                            lanes &&
                            <div className="body-pcl">
                                {
                                    lanes?.map((lane, i) => {
                                        const laneTitle = `${title ?? ''} (${lane.code ?? ''} ${lane.description ? ' - ' + lane.description : ''} )${lane.observations ?? ''}`

                                        return (
                                            <div className="capture-place-lane" key={i}>
                                                <p className="truncate mb-0" title={laneTitle}>{laneTitle}</p>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        }
                    </div>
                );
            }
            case "waze":
                return (
                    <>
                        <p>Endereço: <b>{description.street}</b></p>
                        <ActionButton onClick={() => openWazeMap(marker.getPosition().lat(), marker.getPosition().lng())}>
                            Abrir no Waze Partner Hub
                        </ActionButton>
                    </>
                )
            case "sos":
                return (
                    <>
                        <p>Endereço: <b>{description.address}</b></p>
                        <p>Tipo: <b>{description.address_type}</b></p>
                        <p>Descrição: <b>{description.description}</b></p>
                        <ActionButton onClick={() => navigate(`/sos/citizens/form/${description.citizen_id}`, true)}>
                            Abrir cadastro
                        </ActionButton>
                    </>
                )
            case "encounter":
                return (
                    <>
                        {
                            description.dt_start &&
                            <p>Data: <b>{formatUtcDateTime(description.dt_start)}</b></p>
                        }
                        <p>Justificativa: <b>{description.observation}</b></p>
                        <p>Ações decorrentes: <b>{description.actions}</b></p>
                        <ActionButton onClick={() => navigate(`/encounter/form/${id}`, true)}>
                            Abrir cadastro
                        </ActionButton>
                    </>
                )
        }
    }

    return (
        <div className="w-75 max-w-[calc(100vw-24px)] max-h-[calc(100vh-90px)] flex flex-col bg-white border border-[#dfe5ec] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5">
                <ItemToken item={meta}/>
                <span className="flex-1 min-w-0 truncate text-[12px] font-semibold ">{title}</span>
                <Button
                    onClick={onClose}
                    title="Fechar"
                    target={"primary-outline"}
                    className="flex h-5! w-5! p-0! shrink-0 items-center justify-center rounded text-[#b9c4d6] text-[14px] leading-none cursor-pointer transition-colors"
                >
                    ×
                </Button>
            </div>

            <div className="p-3 overflow-y-auto text-[13px] text-[#16233a]">
                {contentDetails()}
            </div>
        </div>
    )
};

export default MarkerDetails;
